import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import DocumentTitle from 'react-document-title';
import { useParams, useSearchParams } from 'react-router-dom';

import { Container } from '@hivebrite/design-system/Container';
import { Loader } from '@hivebrite/design-system/Loader';
import { useMatchBreakpoint } from '@hivebrite/design-system/Theme';

import { useDonationContext } from 'hb-react/frontoffice/contexts/DonationContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { getPageTitle } from 'hb-react/frontoffice/utils/page';
import Cover from 'hb-react/shared/apps/Donations/components/Cover';
import Form from 'hb-react/shared/apps/Donations/components/Form';
import fetchOrder from 'hb-react/shared/apps/Donations/requests/fetchOrder';
import Grid, { Row, Col } from 'hb-react/shared/components/DesignSystem/Grid';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import { generatePageTitle } from 'hb-react/shared/utils/generatePageTitle';
import { getPageTitleFromNetwork } from 'hb-react/shared/utils/getPageTitleFromNetwork';

// Also used on the BO DonationsPreviewContainer
const DonationsShowContainer = ({ isPreview }) => {
  const [order, setOrder] = useState(null);
  const { currentUser, currentNetwork } = useDefaultAppContexts();
  const { whitelabelURL } = useDonationContext();
  const whitelabelURLEnabled = currentUser ? false : whitelabelURL;
  const params = useParams();
  const isMobile = useMatchBreakpoint(['s']);
  const { flagAnalyticsChangeTitleTags } = useFeatureFlag();

  useEffect(() => {
    const campaignId = params?.campaignId;

    fetchOrder(campaignId, whitelabelURLEnabled, { isPreview }, ({ order }) => {
      setOrder(order);
    });
  }, [params]);

  const [search] = useSearchParams();

  const {
    amount,
    fund_id: fundId,
    external_id: externalId,
    promotion_code: promotionCode,
    monthly_recurring: monthlyRecurring,
  } = Object.fromEntries(search);

  const payLaterEnabled = useMemo(
    () =>
      currentUser === null
        ? order?.campaign?.enable_public_donors_to_commit_and_pay_later
        : order?.campaign?.enable_donors_to_commit_and_pay_later,
    [currentUser, order?.campaign],
  );

  const payNowEnabled = useMemo(
    () =>
      currentUser === null
        ? order?.campaign?.enable_public_donors_to_pay_now
        : order?.campaign?.enable_donors_to_pay_now,
    [currentUser, order?.campaign],
  );

  if (!order) return <Loader size={32} />;

  return (
    <>
      {flagAnalyticsChangeTitleTags ? (
        <DocumentTitle
          title={generatePageTitle({
            title: order.campaign.title,
            networkName: getPageTitleFromNetwork(currentNetwork),
          })}
        />
      ) : (
        <DocumentTitle title={getPageTitle({ title: order.campaign.title })} />
      )}
      <Cover imageUrl={order.campaign.cover_image_url}>
        <Container>
          <Grid width="100%" mx={isMobile ? 0 : undefined}>
            <Row>
              <Col offset={{ _: 0, m: 2 / 12 }} width={{ _: 1, m: 8 / 12 }}>
                <Form
                  order={order}
                  initialAmount={amount}
                  initialFundId={fundId}
                  initialMonthlyRecurring={monthlyRecurring === 'true'}
                  externalId={externalId}
                  promotionCode={promotionCode}
                  submitDisabled={isPreview}
                  payLaterEnabled={payLaterEnabled}
                  payNowEnabled={payNowEnabled}
                />
              </Col>
            </Row>
          </Grid>
        </Container>
      </Cover>
    </>
  );
};

DonationsShowContainer.propTypes = {
  isPreview: PropTypes.bool,
};

DonationsShowContainer.defaultProps = {
  isPreview: false,
};

export default DonationsShowContainer;
